/**
 * Comprehensive Task Form Demo
 *
 * Demonstrates the full capabilities of the Task primitive with real-world scenarios:
 * 1. Life-Critical Healthcare Task (Dual Control + MFA)
 * 2. Financial Approval Task (SOX Compliance)
 * 3. Complex Parent Task with Subtasks
 * 4. Escalation & SLA Monitoring
 * 5. Integration-Based Automated Task
 */
// @ts-nocheck


import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Modal,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconFlame,
  IconHeartbeat,
  IconHierarchy,
  IconMoneybag,
  IconRocket,
  IconTrendingUp,
} from '@tabler/icons-react';
import React, { useState } from 'react';
import type { Task } from '../data/repositories/TaskRepository';
import { ComprehensiveTaskForm } from './ComprehensiveTaskForm';
import { BUILTIN_SCENARIOS, type BuiltinScenario } from './taskFormState';

export const ComprehensiveTaskFormDemo: React.FC = () => {
  const [scenarioOpen, setScenarioOpen] = useState<string | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<'healthcare' | 'financial' | 'hierarchy' | 'escalation' | 'automation'>('healthcare');

  // Build scenario lookup from shared BUILTIN_SCENARIOS
  type ScenarioKey = 'healthcare' | 'financial' | 'hierarchy' | 'escalation' | 'automation';
  const scenarioKeys: ScenarioKey[] = ['healthcare', 'financial', 'hierarchy', 'escalation', 'automation'];
  const scenarios: Record<ScenarioKey, BuiltinScenario> = Object.fromEntries(
    BUILTIN_SCENARIOS.map(s => [s.key, s]),
  ) as Record<ScenarioKey, BuiltinScenario>;

  const scenarioInfo = {
    healthcare: {
      title: '🏥 Life-Critical Healthcare',
      description: 'High-risk medication administration with dual control + MFA',
      icon: IconHeartbeat,
      color: 'red',
      features: [
        'Dual control (two-person rule)',
        'MFA required for both signers',
        'HIPAA & FDA compliance',
        'Complete audit trail',
        'Evidence collection',
        'Life-critical flag',
      ],
    },
    financial: {
      title: '💰 Financial Approval (SOX)',
      description: 'Large wire transfer requiring multi-level approval',
      icon: IconMoneybag,
      color: 'green',
      features: [
        'SOX compliance required',
        'Dual control for large amounts',
        'Escalation chain',
        'Auto-escalate on timeout',
        '7-year retention',
        'Segregation of duties',
      ],
    },
    hierarchy: {
      title: '🏗️ Complex Parent Task',
      description: 'Patient discharge with multiple nested subtasks',
      icon: IconHierarchy,
      color: 'blue',
      features: [
        'Parent-child relationships',
        'Multiple subtasks',
        'Completion strategy',
        'Sequential execution',
        'Dependency management',
        'Total time estimation',
      ],
    },
    escalation: {
      title: '📈 Escalation & SLA',
      description: 'Customer complaint with automatic escalation',
      icon: IconTrendingUp,
      color: 'orange',
      features: [
        '4 escalation rules',
        'Automatic priority upgrade',
        'Escalation chain',
        'SLA monitoring',
        'Stale task detection',
        'Multi-channel notifications',
      ],
    },
    automation: {
      title: '🤖 Automated Integration',
      description: 'System task with retry logic and webhooks',
      icon: IconRocket,
      color: 'purple',
      features: [
        'Automated execution',
        'JavaScript automation script',
        'Retry with exponential backoff',
        'Webhook notifications',
        'External API integration',
        'Error handling',
      ],
    },
  };

  const handleSuccess = (task: Task) => {
    notifications.show({
      title: 'Success! 🎉',
      message: `Task "${task.name}" created successfully`,
      color: 'green',
      icon: <IconCheck />,
    });
    setScenarioOpen(null);
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Paper p="xl" withBorder bg="gradient-to-r from-blue-500 to-purple-600">
          <Group justify="space-between">
            <div>
              <Title order={1} c="white">
                🎯 Comprehensive Task Form Demo
              </Title>
              <Text size="lg" c="white" mt="xs" opacity={0.9}>
                Experience the full power of the Task primitive with 185+ fields
              </Text>
            </div>
            <Badge size="xl" variant="filled" color="yellow">
              PRODUCTION READY
            </Badge>
          </Group>
        </Paper>

        {/* Key Features */}
        <Card withBorder>
          <Title order={3} mb="md">
            ✨ Key Features Demonstrated
          </Title>
          <Grid>
            <Grid.Col span={4}>
              <Paper p="md" withBorder>
                <IconFlame size={32} color="red" />
                <Text fw={600} mt="sm">
                  Life-Critical Safety
                </Text>
                <Text size="sm" c="dimmed">
                  Dual control, MFA, validation
                </Text>
              </Paper>
            </Grid.Col>
            <Grid.Col span={4}>
              <Paper p="md" withBorder>
                <IconHierarchy size={32} color="blue" />
                <Text fw={600} mt="sm">
                  Task Hierarchy
                </Text>
                <Text size="sm" c="dimmed">
                  Parent-child relationships
                </Text>
              </Paper>
            </Grid.Col>
            <Grid.Col span={4}>
              <Paper p="md" withBorder>
                <IconTrendingUp size={32} color="orange" />
                <Text fw={600} mt="sm">
                  Escalation & SLA
                </Text>
                <Text size="sm" c="dimmed">
                  Auto-escalation rules
                </Text>
              </Paper>
            </Grid.Col>
          </Grid>
        </Card>

        {/* Scenario Selector */}
        <Card withBorder>
          <Title order={3} mb="md">
            🎭 Choose a Scenario
          </Title>
          <SegmentedControl
            fullWidth
            value={selectedScenario}
            onChange={(value) => setSelectedScenario(value as ScenarioKey)}
            data={[
              { label: '🏥 Healthcare', value: 'healthcare' },
              { label: '💰 Financial', value: 'financial' },
              { label: '🏗️ Hierarchy', value: 'hierarchy' },
              { label: '📈 Escalation', value: 'escalation' },
              { label: '🤖 Automation', value: 'automation' },
            ]}
            size="lg"
          />
        </Card>

        {/* Scenario Details */}
        {selectedScenario && scenarioInfo[selectedScenario] && (
          <Card withBorder>
            <Group justify="space-between" mb="md">
              <div>
                <Title order={3}>{scenarioInfo[selectedScenario].title}</Title>
                <Text size="sm" c="dimmed">
                  {scenarioInfo[selectedScenario].description}
                </Text>
              </div>
              <Badge size="lg" color={scenarioInfo[selectedScenario].color}>
                {selectedScenario.toUpperCase()}
              </Badge>
            </Group>

            <Grid>
              <Grid.Col span={8}>
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Features Demonstrated:
                  </Text>
                  <Grid>
                    {scenarioInfo[selectedScenario].features.map((feature: string, idx: number) => (
                      <Grid.Col key={idx} span={6}>
                        <Group gap="xs">
                          <IconCheck size={14} color="green" />
                          <Text size="sm">{feature}</Text>
                        </Group>
                      </Grid.Col>
                    ))}
                  </Grid>
                </Stack>
              </Grid.Col>
              <Grid.Col span={4}>
                <Button
                  fullWidth
                  size="lg"
                  leftSection={React.createElement(scenarioInfo[selectedScenario].icon, {
                    size: 20,
                  })}
                  onClick={() => setScenarioOpen(selectedScenario)}
                >
                  Launch Scenario
                </Button>
              </Grid.Col>
            </Grid>
          </Card>
        )}

        {/* Information */}
        <Alert color="blue" icon={<IconAlertCircle />} title="About This Demo">
          <Stack gap="xs">
            <Text size="sm">
              This demo showcases the <strong>Comprehensive Task Form</strong> with 5 real-world
              scenarios. Each scenario demonstrates different aspects of the Task primitive&apos;s 185+
              field capability.
            </Text>
            <Text size="sm">
              <strong>What you can do:</strong>
            </Text>
            <ul style={{ marginTop: 0, paddingLeft: '1.5rem' }}>
              <li>
                <Text size="sm">Create life-critical tasks with dual control & MFA</Text>
              </li>
              <li>
                <Text size="sm">Build complex parent-child task hierarchies</Text>
              </li>
              <li>
                <Text size="sm">Configure automatic escalation rules</Text>
              </li>
              <li>
                <Text size="sm">Set up compliance & regulatory tracking</Text>
              </li>
              <li>
                <Text size="sm">Integrate with external systems via webhooks</Text>
              </li>
            </ul>
            <Text size="sm" mt="xs">
              <strong>Note:</strong> All scenarios use pre-populated data to showcase the form&apos;s
              capabilities. You can modify any field before submitting.
            </Text>
          </Stack>
        </Alert>
      </Stack>

      {/* Scenario Modals */}
      {scenarioKeys.map((key) => (
        <Modal
          key={key}
          opened={scenarioOpen === key}
          onClose={() => setScenarioOpen(null)}
          size="95%"
          title={
            <Group>
              {React.createElement(scenarioInfo[key].icon, { size: 24 })}
              <Text fw={600}>{scenarioInfo[key].title}</Text>
            </Group>
          }
        >
          <ComprehensiveTaskForm
            mode="create"
            {...scenarios[key]}
            onSuccess={handleSuccess}
            onCancel={() => setScenarioOpen(null)}
          />
        </Modal>
      ))}
    </Container>
  );
};

export default ComprehensiveTaskFormDemo;
